import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api, getUser } from '../api';
import { LoadingSpinner, ConfirmDialog, Modal } from './shared';

const icona = (v) => {
  if (v.cartella) return '📁';
  const e = (v.nome.split('.').pop() || '').toLowerCase();
  if (e === 'pdf') return '📄';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'].includes(e)) return '🖼️';
  if (['doc', 'docx', 'odt', 'rtf'].includes(e)) return '📝';
  if (['xls', 'xlsx', 'ods', 'csv'].includes(e)) return '📊';
  if (['zip', 'rar', '7z'].includes(e)) return '🗜️';
  if (['eml', 'msg'].includes(e)) return '✉️';
  return '📎';
};
const dimensione = (n) => {
  if (n == null || n < 0) return '';
  if (n < 1024) return n + ' B';
  if (n < 1048576) return Math.round(n / 1024) + ' KB';
  return (n / 1048576).toFixed(1).replace('.', ',') + ' MB';
};
const dataIt = (s) => { try { return s ? new Date(s).toLocaleDateString('it-IT') : ''; } catch (e) { return ''; } };

// i file spuntati restano selezionati anche cambiando cartella o anno
const chiaveFile = (anno, sub, nome) => anno + '|' + sub + '|' + nome;
const percorsoLeggibile = (f) => [f.anno === 'GENERALE' ? 'Generale' : f.anno, f.sub, f.nome].filter(Boolean).join(' / ');

// ── finestra di invio email ──────────────────────────────────────────────────
function InvioEmail({ clienteId, file, onRimuovi, onClose, onInviato, addToast }) {
  const [info, setInfo] = useState(null);
  const [a, setA] = useState('');
  const [cc, setCc] = useState('');
  const [copiaAMe, setCopiaAMe] = useState(false);
  const [oggetto, setOggetto] = useState('');
  const [testo, setTesto] = useState('');
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState('');

  useEffect(() => {
    let vivo = true;
    api.get(`/api/clienti/${clienteId}/documenti/invio-info`)
      .then(d => {
        if (!vivo) return;
        setInfo(d);
        setA(d.email || d.pec || '');
        setOggetto(d.cliente ? `Documenti – ${d.cliente}` : 'Documenti da Studio SP');
        setTesto('Buongiorno,\n\nin allegato i documenti richiesti.\n\nCordiali saluti,\nStudio SP');
      })
      .catch(e => { if (vivo) setErrore(e.message); });
    return () => { vivo = false; };
  }, [clienteId]);

  const totale = file.reduce((s, f) => s + (f.dimensione > 0 ? f.dimensione : 0), 0);
  const limiti = (info && info.limiti) || { file: 20, byte: 18 * 1024 * 1024 };
  const troppoGrande = totale > limiti.byte;
  const troppiFile = file.length > limiti.file;
  const pronto = info && info.configurata && !troppoGrande && !troppiFile && a.trim() && file.length > 0 && !invio;

  const aggiungi = (indirizzo) => {
    if (!indirizzo) return;
    const gia = a.split(/[;,\s]+/).map(x => x.toLowerCase()).includes(indirizzo.toLowerCase());
    if (!gia) setA(a.trim() ? a.trim().replace(/[;,]$/, '') + ', ' + indirizzo : indirizzo);
  };

  const invia = async () => {
    setErrore(''); setInvio(true);
    try {
      const r = await api.post(`/api/clienti/${clienteId}/documenti/invia-email`, {
        files: file.map(f => ({ anno: f.anno, sub: f.sub, nome: f.nome })),
        a, cc, copiaAMe, oggetto, testo,
      });
      addToast(`Email inviata a ${(r.a || []).join(', ')} con ${r.allegati} allegat${r.allegati === 1 ? 'o' : 'i'}`, 'success');
      onInviato();
    } catch (e) { setErrore(e.message); }
    finally { setInvio(false); }
  };

  return (
    <Modal title="✉ Invia file per email" onClose={invio ? () => {} : onClose}>
      {!info && !errore && <LoadingSpinner />}
      {info && !info.configurata && (
        <div style={{ padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'var(--radius-sm)', color:'#DC2626', fontSize:13, marginBottom:12 }}>
          ⚠ {info.motivo || 'La posta non è configurata.'}
        </div>
      )}
      {info && (
        <div>
          <div className="form-group" style={{ marginBottom:10 }}>
            <label className="form-label">A *</label>
            <input className="form-control" value={a} onChange={e => setA(e.target.value)} placeholder="indirizzo@esempio.it (più indirizzi separati da virgola)" />
            {(info.email || info.pec) && (
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:6 }}>
                {info.email && <button type="button" className="btn btn-sm" onClick={() => aggiungi(info.email)}>＋ Email cliente: {info.email}</button>}
                {info.pec && <button type="button" className="btn btn-sm" onClick={() => aggiungi(info.pec)}>＋ PEC cliente: {info.pec}</button>}
              </div>
            )}
          </div>
          <div className="form-group" style={{ marginBottom:10 }}>
            <label className="form-label">Cc</label>
            <input className="form-control" value={cc} onChange={e => setCc(e.target.value)} placeholder="facoltativo" />
            {info.emailUtente && (
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12.5, marginTop:6, cursor:'pointer' }}>
                <input type="checkbox" checked={copiaAMe} onChange={e => setCopiaAMe(e.target.checked)} />
                Invia una copia anche a me ({info.emailUtente})
              </label>
            )}
          </div>
          <div className="form-group" style={{ marginBottom:10 }}>
            <label className="form-label">Oggetto</label>
            <input className="form-control" value={oggetto} onChange={e => setOggetto(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom:12 }}>
            <label className="form-label">Messaggio</label>
            <textarea className="form-control" rows={6} value={testo} onChange={e => setTesto(e.target.value)} />
          </div>

          <div style={{ fontSize:12.5, fontWeight:600, marginBottom:6 }}>
            Allegati ({file.length}) — {dimensione(totale) || '0 B'}
          </div>
          <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', maxHeight:180, overflowY:'auto', marginBottom:8 }}>
            {file.map(f => (
              <div key={chiaveFile(f.anno, f.sub, f.nome)} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px', borderBottom:'1px solid var(--border)', fontSize:12.5 }}>
                <span style={{ flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={percorsoLeggibile(f)}>{percorsoLeggibile(f)}</span>
                <span style={{ color:'var(--text-faint)' }}>{dimensione(f.dimensione)}</span>
                <button type="button" className="btn btn-sm" title="Togli dall'invio" onClick={() => onRimuovi(f)} disabled={invio}>×</button>
              </div>
            ))}
          </div>
          {troppoGrande && <div style={{ color:'#DC2626', fontSize:12.5, marginBottom:6 }}>⚠ Gli allegati superano il massimo di {dimensione(limiti.byte)} per messaggio: toglierne qualcuno o dividere l'invio in più email.</div>}
          {troppiFile && <div style={{ color:'#DC2626', fontSize:12.5, marginBottom:6 }}>⚠ Massimo {limiti.file} file per messaggio.</div>}
          <div style={{ fontSize:12, color:'var(--text-faint)' }}>I file vengono presi direttamente da OneDrive: non vengono scaricati sul tuo computer.</div>
        </div>
      )}
      {errore && <div style={{ padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'var(--radius-sm)', color:'#DC2626', fontSize:13, marginTop:12 }}>⚠ {errore}</div>}
      <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:16 }}>
        <button type="button" className="btn" onClick={onClose} disabled={invio}>Annulla</button>
        <button type="button" className="btn btn-primary" onClick={invia} disabled={!pronto}>
          {invio ? '⏳ Invio in corso…' : `✉ Invia (${file.length})`}
        </button>
      </div>
    </Modal>
  );
}

export default function DocumentiCliente({ clienteId, addToast }) {
  const [collegate, setCollegate] = useState(null);   // [{anno, percorso}]
  const [sel, setSel] = useState(null);               // 'GENERALE' | '2025'
  const [sub, setSub] = useState('');
  const [voci, setVoci] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [invio, setInvio] = useState(false);
  const [trascina, setTrascina] = useState(false);
  const [daEliminare, setDaEliminare] = useState(null);
  const [scelti, setScelti] = useState({});           // file spuntati, anche di cartelle diverse
  const [mostraInvio, setMostraInvio] = useState(false);
  const fileRef = useRef();
  const puoScrivere = (getUser() || {}).ruolo !== 'readonly';

  // cartelle collegate al cliente
  useEffect(() => {
    setScelti({}); setMostraInvio(false);
    api.get(`/api/clienti/${clienteId}/cartelle`)
      .then(d => {
        const c = d.collegamenti || [];
        setCollegate(c);
        const chiavi = c.map(x => (x.anno === 0 ? 'GENERALE' : String(x.anno)));
        const anno = String(new Date().getFullYear());
        setSel(chiavi.includes(anno) ? anno : (chiavi.find(k => k !== 'GENERALE') || chiavi[0] || null));
      })
      .catch(e => { setCollegate([]); setErrore(e.message); })
      .finally(() => setLoading(false));
  }, [clienteId]);

  const carica = useCallback(() => {
    if (!sel) return;
    setLoading(true); setErrore('');
    api.get(`/api/clienti/${clienteId}/documenti?anno=${encodeURIComponent(sel)}&sub=${encodeURIComponent(sub)}`)
      .then(d => setVoci(d.voci || []))
      .catch(e => { setVoci([]); setErrore(e.message); })
      .finally(() => setLoading(false));
  }, [clienteId, sel, sub]);
  useEffect(() => { carica(); }, [carica]);

  const cambiaBase = (k) => { setSel(k); setSub(''); };
  const vai = (nome) => setSub(sub ? sub + '/' + nome : nome);
  const parti = sub ? sub.split('/') : [];

  // ── selezione file ──
  const file = voci.filter(v => !v.cartella);
  const spuntato = (v) => !!scelti[chiaveFile(sel, sub, v.nome)];
  const tuttiSpuntati = file.length > 0 && file.every(spuntato);
  const alterna = (v) => {
    const k = chiaveFile(sel, sub, v.nome);
    setScelti(prev => {
      const n = { ...prev };
      if (n[k]) delete n[k]; else n[k] = { anno: sel, sub, nome: v.nome, dimensione: v.dimensione };
      return n;
    });
  };
  const alternaTutti = () => {
    setScelti(prev => {
      const n = { ...prev };
      if (file.every(v => n[chiaveFile(sel, sub, v.nome)])) file.forEach(v => { delete n[chiaveFile(sel, sub, v.nome)]; });
      else file.forEach(v => { n[chiaveFile(sel, sub, v.nome)] = { anno: sel, sub, nome: v.nome, dimensione: v.dimensione }; });
      return n;
    });
  };
  const elencoScelti = Object.values(scelti);
  const nScelti = elencoScelti.length;
  const pesoScelti = elencoScelti.reduce((s, f) => s + (f.dimensione > 0 ? f.dimensione : 0), 0);
  const nCartelle = new Set(elencoScelti.map(f => f.anno + '|' + f.sub)).size;
  const rimuoviScelto = (f) => {
    setScelti(prev => { const n = { ...prev }; delete n[chiaveFile(f.anno, f.sub, f.nome)]; return n; });
  };
  useEffect(() => { if (mostraInvio && nScelti === 0) setMostraInvio(false); }, [mostraInvio, nScelti]);

  // apre o scarica un file: chiede un link temporaneo e lo apre in una nuova scheda
  const apri = async (v, scarica) => {
    const w = scarica ? null : window.open('', '_blank');
    try {
      const r = await api.post(`/api/clienti/${clienteId}/documenti/link`, { anno: sel, sub, nome: v.nome });
      const url = r.url + (scarica ? '?download=1' : '');
      if (scarica) { const a = document.createElement('a'); a.href = url; a.download = v.nome; document.body.appendChild(a); a.click(); a.remove(); }
      else if (w) w.location.href = url; else window.location.href = url;
    } catch (e) { if (w) w.close(); addToast(e.message, 'error'); }
  };

  const carcaFile = async (files) => {
    if (!files || !files.length) return;
    setInvio(true);
    try {
      const fd = new FormData();
      fd.append('anno', sel); fd.append('sub', sub);
      [...files].forEach(f => fd.append('file', f));
      const res = await fetch(`/api/clienti/${clienteId}/documenti`, {
        method: 'POST', body: fd, headers: { Authorization: 'Bearer ' + (localStorage.getItem('sp_token') || '') },
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Caricamento non riuscito');
      addToast(j.data.caricati.length === 1 ? `"${j.data.caricati[0]}" caricato` : `${j.data.caricati.length} file caricati`, 'success');
      carica();
    } catch (e) { addToast(e.message, 'error'); }
    finally { setInvio(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const nuovaCartella = async () => {
    const nome = window.prompt('Nome della nuova cartella:');
    if (!nome || !nome.trim()) return;
    try { await api.post(`/api/clienti/${clienteId}/documenti/cartella`, { anno: sel, sub, nome: nome.trim() }); carica(); }
    catch (e) { addToast(e.message, 'error'); }
  };

  const elimina = async () => {
    const v = daEliminare; setDaEliminare(null);
    try {
      await api.post(`/api/clienti/${clienteId}/documenti/elimina`, { anno: sel, sub, nome: v.nome });
      rimuoviScelto({ anno: sel, sub, nome: v.nome });
      addToast('File eliminato', 'success'); carica();
    }
    catch (e) { addToast(e.message, 'error'); }
  };

  if (collegate === null) return <LoadingSpinner />;
  if (!collegate.length) {
    return (
      <div className="empty-state" style={{ padding:'40px 0' }}>
        <div className="empty-state-icon">☁</div>
        <div className="empty-state-title">Nessuna cartella OneDrive collegata</div>
        <div style={{ fontSize:13, color:'var(--text-faint)', marginTop:4 }}>
          Collega le cartelle di questo cliente dalla scheda <b>☁ Cartelle</b>.
        </div>
        {errore && <div style={{ color:'#DC2626', fontSize:12, marginTop:8 }}>{errore}</div>}
      </div>
    );
  }

  const basi = collegate.map(x => [x.anno === 0 ? 'GENERALE' : String(x.anno), x.anno === 0 ? 'Generale' : String(x.anno), x.percorso]);
  basi.sort((a, b) => (a[0] === 'GENERALE' ? -1 : b[0] === 'GENERALE' ? 1 : b[0].localeCompare(a[0])));

  return (
    <div onDragOver={e => { if (puoScrivere) { e.preventDefault(); setTrascina(true); } }}
         onDragLeave={() => setTrascina(false)}
         onDrop={e => { e.preventDefault(); setTrascina(false); if (puoScrivere) carcaFile(e.dataTransfer.files); }}
         style={{ outline: trascina ? '2px dashed var(--accent)' : 'none', borderRadius:'var(--radius-sm)' }}>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12, alignItems:'center' }}>
        {basi.map(([k, titolo]) => (
          <button key={k} onClick={() => cambiaBase(k)} className="btn btn-sm"
            style={{ background: sel === k ? 'var(--accent)' : undefined, color: sel === k ? 'white' : undefined, fontWeight: sel === k ? 600 : 400 }}>
            {titolo}
          </button>
        ))}
        <span style={{ flex:1 }} />
        {puoScrivere && <>
          <input ref={fileRef} type="file" multiple style={{ display:'none' }} onChange={e => carcaFile(e.target.files)} />
          <button className="btn btn-sm" onClick={nuovaCartella}>＋ Nuova cartella</button>
          <button className="btn btn-sm btn-primary" disabled={invio} onClick={() => fileRef.current.click()}>
            {invio ? '⏳ Caricamento…' : '⬆ Carica file'}
          </button>
        </>}
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:4, flexWrap:'wrap', fontSize:13, marginBottom:10 }}>
        <button className="btn btn-sm" onClick={() => setSub('')}>📁 {basi.find(b => b[0] === sel)?.[2] || ''}</button>
        {parti.map((p, i) => (
          <React.Fragment key={i}>
            <span style={{ color:'var(--text-faint)' }}>/</span>
            <button className="btn btn-sm" onClick={() => setSub(parti.slice(0, i + 1).join('/'))}>{p}</button>
          </React.Fragment>
        ))}
        <button className="btn btn-sm" style={{ marginLeft:'auto' }} onClick={carica} title="Ricarica">↻</button>
      </div>

      {errore && <div style={{ padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'var(--radius-sm)', color:'#DC2626', fontSize:13, marginBottom:10 }}>⚠ {errore}</div>}

      {loading ? <LoadingSpinner /> : (
        <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-sm)' }}>
          {sub && (
            <div onClick={() => setSub(parti.slice(0, -1).join('/'))}
              style={{ padding:'9px 14px', borderBottom:'1px solid var(--border)', cursor:'pointer', fontSize:13.5, color:'var(--text-muted)' }}>
              ↩ Indietro
            </div>
          )}
          {puoScrivere && file.length > 0 && (
            <label style={{ display:'flex', alignItems:'center', gap:12, padding:'7px 14px', borderBottom:'1px solid var(--border)', fontSize:12.5, color:'var(--text-muted)', cursor:'pointer' }}>
              <input type="checkbox" checked={tuttiSpuntati} onChange={alternaTutti} aria-label="Seleziona tutti i file di questa cartella" />
              Seleziona tutti i file di questa cartella ({file.length})
            </label>
          )}
          {voci.length === 0 && !errore && (
            <div style={{ padding:'30px 0', textAlign:'center', color:'var(--text-faint)', fontSize:13 }}>
              Cartella vuota{puoScrivere ? ' — trascina qui i file per caricarli' : ''}
            </div>
          )}
          {voci.map(v => (
            <div key={v.nome} style={{ display:'flex', alignItems:'center', gap:12, padding:'9px 14px', borderBottom:'1px solid var(--border)',
              background: !v.cartella && spuntato(v) ? 'var(--accent-light)' : undefined }}>
              {puoScrivere && (v.cartella
                ? <span style={{ width:13, flexShrink:0 }} />
                : <input type="checkbox" checked={spuntato(v)} onChange={() => alterna(v)} aria-label={'Seleziona ' + v.nome} style={{ flexShrink:0, cursor:'pointer' }} />)}
              <span style={{ fontSize:20, flexShrink:0 }}>{icona(v)}</span>
              <div onClick={() => (v.cartella ? vai(v.nome) : apri(v, false))}
                style={{ flex:1, minWidth:0, cursor:'pointer', fontSize:13.5, fontWeight: v.cartella ? 600 : 400, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}
                title={v.nome}>
                {v.nome}
              </div>
              <span style={{ fontSize:12, color:'var(--text-faint)', width:70, textAlign:'right' }}>{dimensione(v.dimensione)}</span>
              <span style={{ fontSize:12, color:'var(--text-faint)', width:80, textAlign:'right' }}>{dataIt(v.modificato)}</span>
              {!v.cartella && (
                <div style={{ display:'flex', gap:4 }}>
                  <button className="btn btn-sm" onClick={() => apri(v, false)}>Apri</button>
                  <button className="btn btn-sm" onClick={() => apri(v, true)} title="Scarica">⬇</button>
                  {puoScrivere && <button className="btn btn-sm btn-danger" onClick={() => setDaEliminare(v)} title="Elimina">×</button>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {nScelti > 0 && (
        <div style={{ position:'sticky', bottom:8, marginTop:10, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap',
          padding:'10px 14px', background:'var(--surface)', border:'1px solid var(--accent)', borderRadius:'var(--radius-sm)', boxShadow:'var(--shadow-md)' }}>
          <span style={{ fontSize:13.5, fontWeight:600 }}>
            {nScelti} file selezionat{nScelti === 1 ? 'o' : 'i'}
          </span>
          <span style={{ fontSize:12.5, color:'var(--text-faint)' }}>
            {dimensione(pesoScelti) || '0 B'}{nCartelle > 1 ? ` · da ${nCartelle} cartelle` : ''}
          </span>
          <span style={{ flex:1 }} />
          <button className="btn btn-sm" onClick={() => setScelti({})}>Deseleziona tutto</button>
          <button className="btn btn-sm btn-primary" onClick={() => setMostraInvio(true)}>✉ Invia per email</button>
        </div>
      )}

      {mostraInvio && nScelti > 0 && (
        <InvioEmail clienteId={clienteId} file={elencoScelti} addToast={addToast}
          onRimuovi={rimuoviScelto} onClose={() => setMostraInvio(false)}
          onInviato={() => { setMostraInvio(false); setScelti({}); }} />
      )}

      {daEliminare && (
        <ConfirmDialog message={`Eliminare "${daEliminare.nome}" da OneDrive? (resta nel cestino di OneDrive)`}
          onConfirm={elimina} onCancel={() => setDaEliminare(null)} />
      )}
    </div>
  );
}
